import { useCallback, useEffect, useRef, useState } from "react";

import { ADVISOR_TIMEOUT_MS } from "@/shared/api/requestTimeout";
import { reportIncident } from "@/shared/lib/telemetry";

/**
 * ADV-LOCK-1(2026-08-18 P0)锁态守卫:**任何**「发请求期间禁用输入」的入口共用这一处。
 *
 * ★病灶:军师对话四个入口(send/newTopic/resolveConfirm/pickStyle)各写一遍
 * `setLoading(true) … finally setLoading(false)`,解锁完全押在 fetch 会 settle 上 ——
 * 请求永不返回时 finally 永不执行,输入框永久禁用,只能刷新页面。console 与 H5 两份 hook
 * 同构同伤(8 个入口全中)。
 *
 * ★两道闸:①httpClient 的 AbortController 超时(正常路径,抛可 catch 错走既有 catch/finally);
 * ②本守卫的看门狗(兜底)——**只认时间**,即便 promise 既不 resolve 也不 reject(第三方拦截、
 * 打过桩的 fetch、abort 事件到不了调用栈)也强制解锁并留痕。
 *
 * ★看门狗晚于客户端超时:正常超时先落地(带真实 traceId 与错误文案),看门狗只在超时也失灵时兜。
 */
export const LOCK_WATCHDOG_MS = ADVISOR_TIMEOUT_MS + 15_000;

/** 看门狗强解时给用户的话:说清「已经解开了、可以重试」,不含技术黑话。 */
export const LOCK_WATCHDOG_TEXT = "网络没回应,已解除锁定,可重试";

export interface LockGuard {
  loading: boolean;
  /**
   * 上锁跑一段:进入即 loading=true,结束(成功/抛错/看门狗到点)必 loading=false。
   * `entry` 是**接口路径**(埋点用,不带 query/用户内容);fn 抛出的错原样透传给调用方。
   */
  runLocked: (entry: string, fn: () => Promise<void>) => Promise<void>;
}

export function useLockGuard(sessionId: string, onWatchdogUnlock: () => void): LockGuard {
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // 回调放 ref:调用方常传内联函数,进依赖会让 runLocked 每帧新建,连累其上游 useCallback。
  const onWatchdogRef = useRef(onWatchdogUnlock);
  onWatchdogRef.current = onWatchdogUnlock;

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    []
  );

  const runLocked = useCallback(
    async (entry: string, fn: () => Promise<void>) => {
      setLoading(true);
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        timerRef.current = null;
        setLoading(false);
        reportIncident({
          event: "chat_lock_watchdog",
          path: entry,
          elapsedMs: LOCK_WATCHDOG_MS,
          sessionId,
        });
        onWatchdogRef.current();
      }, LOCK_WATCHDOG_MS);
      try {
        await fn();
      } finally {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = null;
        setLoading(false);
      }
    },
    [sessionId]
  );

  return { loading, runLocked };
}
